/**
 * passkeyCeremony — the browser half of the door: one callable, three actions.
 *
 * `enrollPasskey` mints a challenge, has the platform authenticator create an
 * ES256 discoverable credential bound to the signed-in account, and hands the
 * attestation to the door. `signInWithPasskey` mints a challenge, has the
 * authenticator sign it, hands the assertion to the door and signs the Firebase
 * session in with the custom token it answers — so a caller holds a session
 * when the promise resolves, and whatever else the product's `mintSession`
 * added rides beside `token` in the resolved value.
 *
 * Every failure propagates as thrown: the browser's own `DOMException`
 * (`NotAllowedError` — the prompt was closed, timed out, or no credential
 * answered) and the callable's `invalid-argument/invalid-or-expired` are the
 * two a product tells apart; neither is reshaped here, so a product reads the
 * SDK's own names.
 */
import { type Auth, signInWithCustomToken } from 'firebase/auth';
import { type Functions, httpsCallable } from 'firebase/functions';
import {
  type IdentityPasskeyInput,
  IdentityPasskeyInput as IdentityPasskeyInputSchema,
  type IdentityPasskeyOutput,
  type PasskeyChallengeInput,
  PasskeyChallengeOutput,
  VerifyPasskeyEnrollmentOutput,
  VerifyPasskeySignInOutput,
} from '../../types/src/index.ts';

function toBase64Url(bytes: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replaceAll('=', '');
}

export type PasskeyCeremonyOptions = {
  auth: Auth;
  functions: Functions;
  /** The deployed callable's name — the product's mount of `passkeyDoor`. */
  callable: string;
  /** The relying party's display name, shown by the OS prompt. */
  rpName: string;
};

export type SignInWithPasskeyOptions = {
  /**
   * `'conditional'` offers a saved passkey in a field's own autofill
   * (`autocomplete="… webauthn"`) and resolves only once one is picked; a
   * modal request that follows must abort it first, so pair with `signal`.
   */
  mediation?: 'conditional';
  signal?: AbortSignal;
};

export function passkeyCeremony({ auth, functions, callable, rpName }: PasskeyCeremonyOptions) {
  const door = httpsCallable<IdentityPasskeyInput, IdentityPasskeyOutput>(functions, callable);

  const mintChallenge = (ceremony: PasskeyChallengeInput['ceremony']): Promise<Uint8Array<ArrayBuffer>> =>
    door(IdentityPasskeyInputSchema.parse({ action: 'passkeyChallenge', ceremony }))
      .then((answer) => PasskeyChallengeOutput.parse(answer.data).challenge)
      .then((challenge) =>
        Uint8Array.from(
          atob(
            challenge
              .padEnd(Math.ceil(challenge.length / 4) * 4, '=')
              .replaceAll('-', '+')
              .replaceAll('_', '/'),
          ),
          (char) => char.charCodeAt(0),
        ),
      );

  return {
    enrollPasskey: async (): Promise<void> => {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Passkey enrolment needs a signed-in session');

      const credential = await navigator.credentials.create({
        publicKey: {
          challenge: await mintChallenge('enroll'),
          // The browser and the verifier derive the relying-party id from the same origin.
          rp: { name: rpName },
          // Labels the OS prompt; the door binds the credential to the authenticated uid.
          user: {
            id: new TextEncoder().encode(currentUser.uid),
            name: currentUser.email ?? currentUser.uid,
            displayName: currentUser.displayName ?? currentUser.email ?? currentUser.uid,
          },
          // ES256 is the verifier's only admitted algorithm; widening this changes the security boundary.
          pubKeyCredParams: [{ type: 'public-key', alg: -7 }],
          authenticatorSelection: {
            residentKey: 'required',
            userVerification: 'required',
            authenticatorAttachment: 'platform',
          },
          attestation: 'none',
        },
      });

      if (!(credential instanceof PublicKeyCredential)) {
        throw new Error('The authenticator returned no passkey');
      }
      if (!(credential.response instanceof AuthenticatorAttestationResponse)) {
        throw new Error('The authenticator returned no attestation');
      }

      VerifyPasskeyEnrollmentOutput.parse(
        (
          await door(
            IdentityPasskeyInputSchema.parse({
              action: 'verifyPasskeyEnrollment',
              credentialId: toBase64Url(credential.rawId),
              attestationObject: toBase64Url(credential.response.attestationObject),
              clientDataJSON: toBase64Url(credential.response.clientDataJSON),
            }),
          )
        ).data,
      );
    },

    signInWithPasskey: async ({
      mediation,
      signal,
    }: SignInWithPasskeyOptions = {}): Promise<VerifyPasskeySignInOutput> => {
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge: await mintChallenge('signIn'),
          userVerification: 'required',
        },
        ...(mediation ? { mediation } : {}),
        ...(signal ? { signal } : {}),
      });

      if (!(credential instanceof PublicKeyCredential)) {
        throw new Error('The authenticator returned no passkey');
      }
      if (!(credential.response instanceof AuthenticatorAssertionResponse)) {
        throw new Error('The authenticator returned no assertion');
      }

      const session = VerifyPasskeySignInOutput.parse(
        (
          await door(
            IdentityPasskeyInputSchema.parse({
              action: 'verifyPasskeySignIn',
              credentialId: toBase64Url(credential.rawId),
              authenticatorData: toBase64Url(credential.response.authenticatorData),
              clientDataJSON: toBase64Url(credential.response.clientDataJSON),
              signature: toBase64Url(credential.response.signature),
              ...(credential.response.userHandle
                ? { userHandle: toBase64Url(credential.response.userHandle) }
                : {}),
            }),
          )
        ).data,
      );
      await signInWithCustomToken(auth, session.token);
      return session;
    },
  };
}

export type PasskeyCeremony = ReturnType<typeof passkeyCeremony>;
